
import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface AdminUser {
  id: string;
  email: string;
  name: string;
}

interface AdminAuthContextType {
  adminUser: AdminUser | null;
  adminLoading: boolean;
  loginAsAdmin: (email: string, password: string) => Promise<AdminUser | null>;
  logoutAdmin: () => void;
}

// List of admin users
const ADMIN_USERS = [
  'sashank@krix.app',
  'siddhartha@krix.app'
];

// Admin password - in a real app, this should be securely stored
const ADMIN_PASSWORD = 'Banjara@34';

// Default context values
const defaultAdminAuthContext: AdminAuthContextType = {
  adminUser: null,
  adminLoading: true,
  loginAsAdmin: async () => null,
  logoutAdmin: () => {},
};

const AdminAuthContext = createContext<AdminAuthContextType>(defaultAdminAuthContext);

export const useAdminAuth = () => useContext(AdminAuthContext);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [adminLoading, setAdminLoading] = useState(true);

  // Login function specifically for admins
  const loginAsAdmin = async (email: string, password: string): Promise<AdminUser | null> => {
    try {
      console.log('Admin login attempt for:', email);
      
      // Check if email is in the ADMIN_USERS list
      if (!ADMIN_USERS.includes(email)) {
        console.log('Not an admin email:', email);
        toast.error('This email is not authorized for admin access');
        return null;
      }
      
      // Verify the admin password
      if (password !== ADMIN_PASSWORD) {
        console.log('Invalid admin password');
        toast.error('Invalid admin password');
        return null;
      }
      
      // Admin authentication successful
      console.log('Admin authentication successful');
      
      // For an actual Supabase implementation, we'd use a different approach
      // But for now, we'll store admin info in session storage
      const admin: AdminUser = {
        id: email.replace('@', '-').replace('.', '-'),
        email,
        name: 'Admin User',
      };
      
      // Store admin in state and session storage
      setAdminUser(admin);
      sessionStorage.setItem('krix_admin', JSON.stringify(admin));
      toast.success('Admin login successful');
      
      return admin;
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error('Admin login failed. Please try again.');
      return null;
    }
  };

  // Logout function for admins
  const logoutAdmin = () => {
    setAdminUser(null);
    sessionStorage.removeItem('krix_admin');
    toast.success('Admin logged out');
  };

  // Check if admin is logged in from session storage
  useEffect(() => {
    const checkAdminSession = () => {
      try {
        const storedAdmin = sessionStorage.getItem('krix_admin');
        if (storedAdmin) {
          console.log('Found admin in session storage');
          const parsedAdmin = JSON.parse(storedAdmin);
          setAdminUser(parsedAdmin);
        }
      } catch (error) {
        console.error('Error checking admin session:', error);
      } finally {
        setAdminLoading(false);
      }
    };
    
    checkAdminSession();
  }, []);

  return (
    <AdminAuthContext.Provider
      value={{
        adminUser,
        adminLoading,
        loginAsAdmin,
        logoutAdmin,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};
